import {
  getChainKeyFromProtocol,
  ProtocolKeyService,
  ProtocolState,
  USD_ZERO,
} from "@silo/types";
import { Cause, Console, Effect, pipe } from "effect";
import { ContractsClient } from "../contracts-client";
import { getProtocolConfig } from "@silo/config";
import { KvClient } from "../kv-client";
import { isNullable } from "effect/Predicate";

export type GetProtocolState = ReturnType<typeof getProtocolState>;
export type GetProtocolStateSuccess = Effect.Effect.Success<GetProtocolState>;
export type GetProtocolStateError = Effect.Effect.Error<GetProtocolState>;
export type GetProtocolStateContext = Effect.Effect.Context<GetProtocolState>;

export function getProtocolState() {
  return Effect.gen(function* () {
    const [quotePriceUsd, protocolShareFee, rewardTokenPriceUsd] =
      yield* Effect.all(
        [
          ContractsClient.getQuotePrice(),
          ContractsClient.getProtocolShareFee(),
          pipe(
            getRewardTokenPriceUsd(),
            Effect.tapError(() =>
              Effect.logWarning(
                "Failed ❌: get protocol state reward token price USD - falling back to $0 price"
              )
            ),
            Effect.tapErrorCause((cause) => Console.log(Cause.pretty(cause))),
            Effect.catchAll(() => Effect.succeed(USD_ZERO))
          ),
        ],
        { concurrency: "unbounded" }
      );

    const protocolState: ProtocolState = {
      quotePriceUsd,
      protocolShareFee,
      rewardTokenPriceUsd,
    };

    return protocolState;
  });
}

export type GetRewardTokenPriceUsd = ReturnType<typeof getRewardTokenPriceUsd>;
export type GetRewardTokenPriceUsdSuccess =
  Effect.Effect.Success<GetRewardTokenPriceUsd>;
export type GetRewardTokenPriceUsdError =
  Effect.Effect.Error<GetRewardTokenPriceUsd>;
export type GetRewardTokenPriceUsdContext =
  Effect.Effect.Context<GetRewardTokenPriceUsd>;

export function getRewardTokenPriceUsd() {
  return Effect.gen(function* () {
    const protocolKey = yield* ProtocolKeyService;

    const { nativeRewards } = getProtocolConfig(protocolKey);

    if (isNullable(nativeRewards)) return USD_ZERO;

    const chainKey = getChainKeyFromProtocol(protocolKey);
    const { tokenAddress } = nativeRewards;

    const tokenPriceMap = yield* KvClient.getTokenPriceMap([
      { tokenAddress, chainKey },
    ]);

    const priceUsd = yield* tokenPriceMap.get({ tokenAddress, chainKey });

    return priceUsd;
  });
}
